import React, { useEffect, useState } from 'react';
import { API_BASE, authHeaders } from '../../utils/api';
import '../../styles/admin-common.css';
import './PlacedStudents.css';

const PlacedStudents = () => {
  const [placedStudents, setPlacedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    student_name: '',
    course: '',
    placed_company: '',
    company_name: '',
    passout_year: '',
    student_photo: null,
  });
  const [editingId, setEditingId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [userRole, setUserRole] = useState('');
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || '');
      } catch (e) {
        console.error('Error parsing token', e);
      }
    }
    fetchPlacedStudents();
  }, [statusFilter]);

  const fetchPlacedStudents = async () => {
    setLoading(true);
    setError('');
    try {
      let url = `${API_BASE}/api/admin/placed-students`;
      if (statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }
      const res = await fetch(url, { headers: authHeaders() });
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}. ${text.substring(0, 80)}`);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load placed students');
      setPlacedStudents(data.placedStudents || []);
    } catch (err) {
      setError(err.message || 'Failed to load placed students');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 100 * 1024) {
        setError('Image size must be less than 100 KB');
        return;
      }
      setForm((prev) => ({ ...prev, student_photo: file }));
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setForm({
      student_name: '',
      course: '',
      company_name: '',
      passout_year: '',
      student_photo: null,
    });
    setEditingId(null);
    setPreviewImage(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.student_name || !form.course || !form.company_name || !form.passout_year) {
      setError('All fields are required');
      return;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== null && v !== undefined) formData.append(k, v);
    });

    try {
      const url = editingId
        ? `${API_BASE}/api/admin/placed-students/${editingId}`
        : `${API_BASE}/api/admin/placed-students`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: formData,
      });

      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save placed student');

      setSuccess(data.message || 'Saved');
      resetForm();
      await fetchPlacedStudents();
    } catch (err) {
      setError(err.message || 'Failed to save placed student');
    }
  };

  const handleEdit = (item) => {
    setForm({
      student_name: item.student_name,
      course: item.course,
      company_name: item.company_name,
      passout_year: item.passout_year,
      student_photo: null,
    });
    setEditingId(item.id);
    setPreviewImage(item.student_photo ? `${API_BASE}${item.student_photo}` : null);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/placed-students/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to delete');
      setSuccess('Deleted');
      await fetchPlacedStudents();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleApprove = async (id) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/placed-students/${id}/approve`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to approve');
      setSuccess('Approved');
      await fetchPlacedStudents();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this entry?')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/placed-students/${id}/reject`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to reject');
      setSuccess('Rejected');
      await fetchPlacedStudents();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/placed-students/export/csv`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `placed_students_${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
      setSuccess('Exported successfully');
    } catch (err) { setError(err.message); } finally { setExporting(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { setError('Please select a CSV file'); return; }
    setImporting(true); setError(''); setSuccess('');
    const formData = new FormData(); formData.append('csv_file', file);
    try {
      const res = await fetch(`${API_BASE}/api/admin/placed-students/import/csv`, { method: 'POST', headers: authHeaders(), body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess(data.message);
      if (data.errors?.length) setError(`Some rows failed: ${data.errors.join(', ')}`);
      await fetchPlacedStudents();
    } catch (err) { setError(err.message); } finally { setImporting(false); e.target.value = ''; }
  };

  const downloadSample = () => {
    const csv = `student_name,course,company_name,passout_year\nJohn Doe,B.Tech Marine Engineering,Maersk Line,2023-2024\nJane Smith,B.Tech Naval Architecture,Shipping Corp,2022-2023`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'placed_students_sample.csv';
    document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
  };

  const pending = placedStudents.filter((s) => s.status === 'pending');
  const approved = placedStudents.filter((s) => s.status === 'approved');

  return (
    <div className="admin-page placed-students-admin-page">
      <div className="page-heading">
        <h1>Placed Students</h1>
        <p>Manage placed students data with approval workflow.</p>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}

      <div className="admin-actions">
        <button onClick={handleExport} disabled={exporting} className="btn-secondary">{exporting ? 'Exporting...' : '📥 Export CSV'}</button>
        <label className="btn-secondary" style={{ cursor: 'pointer' }}>
          {importing ? 'Importing...' : '📤 Import CSV'}
          <input type="file" accept=".csv" onChange={handleImport} disabled={importing} style={{ display: 'none' }} />
        </label>
        <button onClick={downloadSample} className="btn-secondary">📋 Download Sample CSV</button>
      </div>

      <div className="admin-grid placed-students-grid">
        <div className="card">
          <h2>{editingId ? 'Edit Placed Student' : 'Add Placed Student'}</h2>
          <form className="admin-form placed-student-form" onSubmit={handleSubmit}>
            <label>
              Student Name *
              <input name="student_name" value={form.student_name} onChange={handleChange} required />
            </label>
            <label>
              Course *
              <input name="course" value={form.course} onChange={handleChange} required placeholder="e.g., B.Tech Marine Engineering" />
            </label>
            <label>
              Company Name *
              <input name="company_name" value={form.company_name} onChange={handleChange} required placeholder="e.g., Maersk Line" />
            </label>
            {/* <label>
              Passout Year *
              <input type="number" name="passout_year" value={form.passout_year} onChange={handleChange} required min="2000" max="2100" />
            </label> */}
            <label>
  Passout Year *
  <input
    type="text"
    name="passout_year"
    value={form.passout_year}
    onChange={handleChange}
    required
    placeholder="e.g. 2024-2027"
    pattern="\d{4}-\d{4}"
    title="Enter year range like 2024-2027"
  />
</label>

            <label>
              Student Photo (Max 100 KB)
              <input type="file" accept="image/*" onChange={handleImageChange} />
              {previewImage && (
                <div className="image-preview">
                  <img src={previewImage} alt="Preview" />
                  <p>Will be resized to 200x200</p>
                </div>
              )}
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{editingId ? 'Update' : 'Add'}</button>
              {editingId && (
                <button type="button" className="btn-secondary" onClick={resetForm}>Cancel</button>
              )}
            </div>
          </form>
        </div>

        <div className="card placed-students-list-card">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
            <h2>Placed Students Entries</h2>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px'}}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          {loading ? (
            <p>Loading...</p>
          ) : placedStudents.length === 0 ? (
            <p>No entries yet.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Course</th>
                  <th>Company</th>
                  <th>Year</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {placedStudents.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.student_photo ? (
                        <img src={`${API_BASE}${s.student_photo}`} alt={s.student_name} className="student-thumb" />
                      ) : (
                        <div className="student-thumb-placeholder">No Photo</div>
                      )}
                    </td>
                    <td>{s.student_name}</td>
                    <td>{s.course}</td>
                    <td>{s.company_name}</td>
                    <td>{s.passout_year}</td>
                    <td><span className={`status status-${s.status}`}>{s.status}</span></td>
                    <td className="actions">
                      <button className="btn-edit" onClick={() => handleEdit(s)}>✏️ Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(s.id)}>🗑️ Delete</button>
                      {userRole === 'superadmin' && s.status === 'pending' && (
                        <button className="btn-edit" onClick={() => handleApprove(s.id)}>✅ Approve</button>
                      )}
                      {userRole === 'superadmin' && s.status === 'pending' && (
                        <button className="btn-delete" onClick={() => handleReject(s.id)}>❌ Reject</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlacedStudents;

